// src/admin/pages/cost/costService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  Cost,
  CostFormData,
  CostStats,
  CostStatus,
  CostType,
  RecurrenceFrequency,
  OCCURRENCES_PER_YEAR,
} from './cost';

const COST_COLLECTION = 'costs';

export interface PaginatedCostsResult {
  costs: Cost[];
  totalCount: number;
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

const mapDocToCost = (docSnap: QueryDocumentSnapshot<DocumentData>): Cost => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    title: data.title,
    category: data.category,
    costType: data.costType,
    amount: data.amount || 0,
    incurredDate: data.incurredDate?.toDate() || new Date(),
    recurrenceFrequency: data.recurrenceFrequency || undefined,
    endDate: data.endDate?.toDate() || null,
    status: data.status,
    vendor: data.vendor || '',
    notes: data.notes || '',
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    createdBy: data.createdBy,
    createdByName: data.createdByName,
  };
};

export class CostService {
  static async createCost(
    formData: CostFormData,
    userId: string,
    userName: string
  ): Promise<string> {
    try {
      const amount = parseFloat(formData.amount) || 0;
      if (amount <= 0) throw new Error('Amount must be greater than zero');
      if (formData.costType === CostType.RECURRING && !formData.recurrenceFrequency) {
        throw new Error('Recurrence frequency is required for recurring costs');
      }

      const costData = {
        title: formData.title.trim(),
        category: formData.category,
        costType: formData.costType,
        amount,
        incurredDate: Timestamp.fromDate(new Date(formData.incurredDate)),
        recurrenceFrequency: formData.costType === CostType.RECURRING
          ? formData.recurrenceFrequency
          : null,
        endDate: formData.endDate ? Timestamp.fromDate(new Date(formData.endDate)) : null,
        status: formData.status,
        vendor: formData.vendor?.trim() || '',
        notes: formData.notes?.trim() || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        createdByName: userName,
      };

      const docRef = await addDoc(collection(db, COST_COLLECTION), costData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating cost:', error);
      throw error;
    }
  }

  static async updateCost(costId: string, formData: CostFormData): Promise<void> {
    try {
      const amount = parseFloat(formData.amount) || 0;
      if (amount <= 0) throw new Error('Amount must be greater than zero');
      if (formData.costType === CostType.RECURRING && !formData.recurrenceFrequency) {
        throw new Error('Recurrence frequency is required for recurring costs');
      }

      const updateData = {
        title: formData.title.trim(),
        category: formData.category,
        costType: formData.costType,
        amount,
        incurredDate: Timestamp.fromDate(new Date(formData.incurredDate)),
        recurrenceFrequency: formData.costType === CostType.RECURRING
          ? formData.recurrenceFrequency
          : null,
        endDate: formData.endDate ? Timestamp.fromDate(new Date(formData.endDate)) : null,
        status: formData.status,
        vendor: formData.vendor?.trim() || '',
        notes: formData.notes?.trim() || '',
        updatedAt: serverTimestamp(),
      };

      const costRef = doc(db, COST_COLLECTION, costId);
      await updateDoc(costRef, updateData);
    } catch (error) {
      console.error('Error updating cost:', error);
      throw error;
    }
  }

  static async deleteCost(costId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COST_COLLECTION, costId));
    } catch (error) {
      console.error('Error deleting cost:', error);
      throw error;
    }
  }

  static async getCost(costId: string): Promise<Cost | null> {
    try {
      const docSnap = await getDoc(doc(db, COST_COLLECTION, costId));
      if (!docSnap.exists()) return null;
      return mapDocToCost(docSnap as QueryDocumentSnapshot<DocumentData>);
    } catch (error) {
      console.error('Error fetching cost:', error);
      throw error;
    }
  }

  /* ===================== GET COSTS PAGINATED =====================
   * Same seek-based pagination pattern used across the admin (see
   * purchaseOrderService.getPurchaseOrdersPaginated): any page can be
   * fetched in any order without needing cursors passed back in.
   * ================================================================= */
  static async getCostsPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    categoryFilter?: string,
    typeFilter?: string,
    statusFilter?: string
  ): Promise<PaginatedCostsResult> {
    try {
      const costs: Cost[] = [];
      const collectionRef = collection(db, COST_COLLECTION);

      const filterConstraints: any[] = [];
      if (categoryFilter) filterConstraints.push(where('category', '==', categoryFilter));
      if (typeFilter) filterConstraints.push(where('costType', '==', typeFilter));
      if (statusFilter) filterConstraints.push(where('status', '==', statusFilter));

      const orderConstraint = orderBy('incurredDate', 'desc');

      const countSnapshot = await getCountFromServer(query(collectionRef, ...filterConstraints));
      const totalCount = countSnapshot.data().count;

      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      snapshot.docs.forEach(docSnap => {
        const cost = mapDocToCost(docSnap);
        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            cost.title.toLowerCase().includes(searchLower) ||
            (cost.vendor && cost.vendor.toLowerCase().includes(searchLower)) ||
            (cost.notes && cost.notes.toLowerCase().includes(searchLower));
          if (matchesSearch) costs.push(cost);
        } else {
          costs.push(cost);
        }
      });

      return { costs, totalCount, lastVisible: null, firstVisible: null };
    } catch (error) {
      console.error('Error fetching paginated costs:', error);
      throw error;
    }
  }

  /* ===================== GET COST STATS =====================
   * Uses getCountFromServer() for count-only fields. The total-amount
   * aggregates require reading documents, fetched once here.
   * ============================================================= */
  static async getCostStats(): Promise<CostStats> {
    try {
      const col = collection(db, COST_COLLECTION);

      const [
        totalSnap,
        oneTimeSnap,
        recurringSnap,
        activeSnap,
        pausedSnap,
        endedSnap,
        docsSnap,
      ] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('costType', '==', CostType.ONE_TIME))),
        getCountFromServer(query(col, where('costType', '==', CostType.RECURRING))),
        getCountFromServer(query(col, where('status', '==', CostStatus.ACTIVE))),
        getCountFromServer(query(col, where('status', '==', CostStatus.PAUSED))),
        getCountFromServer(query(col, where('status', '==', CostStatus.ENDED))),
        getDocs(query(col)),
      ]);

      let totalAmountAllTime = 0;
      let totalAmountThisMonth = 0;
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      docsSnap.forEach(d => {
        const data = d.data();
        const amount = data.amount || 0;
        totalAmountAllTime += amount;

        const incurredDate: Date | undefined = data.incurredDate?.toDate();
        if (data.costType === CostType.ONE_TIME) {
          if (incurredDate && incurredDate >= monthStart) {
            totalAmountThisMonth += amount;
          }
        } else if (data.costType === CostType.RECURRING && data.status === CostStatus.ACTIVE) {
          // Recurring + active costs are counted as incurred this month
          // regardless of exact due date — a simple "currently running" view.
          const started = !incurredDate || incurredDate <= now;
          const notEnded = !data.endDate || data.endDate.toDate() >= monthStart;
          if (started && notEnded) {
            totalAmountThisMonth += amount;
          }
        }
      });

      return {
        totalCount: totalSnap.data().count,
        oneTimeCount: oneTimeSnap.data().count,
        recurringCount: recurringSnap.data().count,
        activeCount: activeSnap.data().count,
        pausedCount: pausedSnap.data().count,
        endedCount: endedSnap.data().count,
        totalAmountAllTime,
        totalAmountThisMonth,
      };
    } catch (error) {
      console.error('Error getting cost stats:', error);
      throw error;
    }
  }

  // Annualized cost for a single recurring entry — used by the UI to show
  // "~X / year" next to a recurring cost's per-occurrence amount.
  static annualizedAmount(amount: number, frequency?: RecurrenceFrequency): number {
    if (!frequency) return amount;
    return amount * OCCURRENCES_PER_YEAR[frequency];
  }
}
